import {
  EmptyState,
  EmptyStateTitle,
  FormattedRelativeTime,
  Stack,
  Text,
  Box,
  InlineStack,
  PlainLink,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import type { BackfillComputed } from '@/features/tasks';
import { TaskStateBadge } from '@/features/tasks';
import { generatePath } from '@/services/router/generate-path';
import * as styles from './tasks-list.css';

export interface TasksListProps {
  tasks: BackfillComputed[];
  isLoading: boolean;
  workspaceId: number | null | undefined;
  onClose: () => void;
}

export function TasksList({ tasks, isLoading, workspaceId, onClose }: TasksListProps) {
  if (isLoading) {
    return (
      <Box p="200" display="flex" justifyContent="center">
        <Text color="subtle">Loading tasks...</Text>
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle icon="check-circle">No new tasks</EmptyStateTitle>
      </EmptyState>
    );
  }

  return (
    <Stack gap="0" overflowY="auto" className={styles.scrollContainer}>
      {tasks.map((task) => {
        const lastUpdate = task.completed ?? task.started ?? task.created;
        const hasObjectName = !isEmptyString(task.object_name);
        const hasObjectId = task.object_id != null;
        const displayText = hasObjectName ? task.object_name : task.object_id;

        const tasksPageUrl = generatePath(
          '/workspaces/:workspaceId/tasks',
          { workspaceId: workspaceId ?? 0 },
          { expand: task.id },
        );

        return (
          <PlainLink href={tasksPageUrl} key={task.id} p="200" className={styles.taskItem} onClick={onClose}>
            <Stack gap="050">
              <InlineStack gap="050" alignBlock="center">
                <FormattedRelativeTime date={lastUpdate * 1000} size="xs" color="subtle" />
                <TaskStateBadge state={task.state} />
              </InlineStack>
              <Text size="s" weight="semibold">
                {task.info.label}
              </Text>

              {!isEmptyString(task.info.description) ? (
                <Text size="xs" color="subtle">
                  {task.info.description}
                </Text>
              ) : hasObjectName || hasObjectId ? (
                <Text size="xs" color="subtle">
                  {displayText}
                  {task.object_deleted === true ? ' (deleted)' : ''}
                </Text>
              ) : null}
            </Stack>
          </PlainLink>
        );
      })}
    </Stack>
  );
}
